import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    type RuleCompletionInfo,
    type RuleSetDisplay,
    RuleType,
} from '@fruitful/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type LayoutChangeEvent, Pressable } from 'react-native';
import Animated, {
    Easing,
    interpolate,
    interpolateColor,
    useAnimatedProps,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withSpring,
    withTiming,
} from 'react-native-reanimated';
import Svg, { Defs, LinearGradient, Path, Stop } from 'react-native-svg';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Icon,
    isWeb,
    LabelEmphasized,
    LabelRegular,
    Shadows,
    Slider,
    SuperscriptEmphasized,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { getMinSpendCents, MIN_GOALS_CENTS } from '../NewMoneyMapVisual';
import {
    BG_FADE_DURATION,
    buildAmortizationFillPath,
    buildAmortizationPath,
    buildGoalsFillPath,
    buildGoalsLinePath,
    computeDurationLabel,
    computeTimeAxis,
    CONTENT_SLIDE_DISTANCE,
    CONTENT_SLIDE_DURATION,
    CONTENT_STAGGER_DELAY,
    DEBT_BG_DARK,
    DEBT_BG_LIGHT,
    DEBT_ICONS,
    DEBT_LABEL,
    DEBT_LINE,
    getDebtData,
    getGoalsLineData,
    getRuleColors,
    getRuleIcon,
    type GoalIconPosition,
    GOALS_BG_DARK,
    GOALS_BG_LIGHT,
    GOALS_ICONS,
    GOALS_LABEL,
    GOALS_LINE,
    goalsValueAtMonth,
    GRAPH_HEIGHT,
    GRAPH_PADDING_BOTTOM,
    GRAPH_PADDING_LEFT,
    GRAPH_PADDING_RIGHT,
    GRAPH_PADDING_TOP,
    ICON_SELECTED_SIZE,
    ICON_SIZE,
    LABEL_SLIDE_DISTANCE,
    LABEL_SLIDE_DURATION,
    LINE_DRAW_DURATION,
    parseDate,
    splitCompletionDate,
    Y_AXIS_HEADROOM,
} from './onboardingMoneyMapUtils';

const AnimatedPath = Animated.createAnimatedComponent(Path);

const AMORTIZATION_POINTS = 60;
const GRAPH_RESHAPE_MS = 300;

const computeAmortizationPoints = (
    startBalance: number,
    annualRate: number,
    totalMonths: number,
    numPoints: number,
): number[] => {
    const r = annualRate / 12;

    if (r < 0.0001) {
        return Array.from({ length: numPoints }, (_, i) => {
            const t = i / (numPoints - 1);
            return startBalance * (1 - t);
        });
    }

    const rN = (1 + r) ** totalMonths;
    const payment = (startBalance * r * rN) / (rN - 1);

    return Array.from({ length: numPoints }, (_, i) => {
        const month = (i / (numPoints - 1)) * totalMonths;
        const rn = (1 + r) ** month;
        const balance = startBalance * rn - payment * ((rn - 1) / r);
        return Math.max(0, balance);
    });
};

export const TimelineGoalRow = ({
    rule,
    isLast,
    isFirst = false,
    selected = false,
    debtPayoffMonths,
}: {
    rule: RuleCompletionInfo;
    isLast: boolean;
    isFirst?: boolean;
    selected?: boolean;
    debtPayoffMonths?: number | null;
}) => {
    const { bg, dark, lightest } = getRuleColors(rule.type);
    const icon = getRuleIcon(rule.type);

    const effectiveDate = React.useMemo(() => {
        if (rule.completionDate != null) return rule.completionDate;
        if (
            rule.type === RuleType.DebtPayDown &&
            debtPayoffMonths != null &&
            debtPayoffMonths > 0
        ) {
            const d = new Date();
            d.setMonth(d.getMonth() + debtPayoffMonths);
            return d.toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
            });
        }
        return null;
    }, [rule.completionDate, rule.type, debtPayoffMonths]);

    const durationLabel = computeDurationLabel(effectiveDate);
    const dateParts = splitCompletionDate(
        rule.completionLabel ?? effectiveDate,
    );

    const amountLabel =
        rule.yearlyContributionMaxCents != null &&
        rule.yearlyContributionMaxCents > 0
            ? `${centsToFormattedDollars(rule.yearlyContributionMaxCents, false)}/YR`
            : rule.targetBalanceCents != null && rule.targetBalanceCents > 0
              ? centsToFormattedDollars(rule.targetBalanceCents, false)
              : null;

    return (
        <XStack
            gap={16}
            alignItems="center"
            paddingVertical={12}
            paddingHorizontal={16}
            marginHorizontal={-16}
            width="calc(100% + 32px)"
            backgroundColor={selected ? lightest : 'transparent'}
            style={{
                transition: 'background-color 200ms ease',
            }}
        >
            {/* Per-row vertical connector segment */}
            {!isFirst && !isLast && (
                <View
                    position="absolute"
                    left={27}
                    top={0}
                    bottom={0}
                    width={2}
                    backgroundColor="$gray400"
                    zIndex={1}
                />
            )}
            {isFirst && !isLast && (
                <View
                    position="absolute"
                    left={27}
                    top="50%"
                    bottom={0}
                    width={2}
                    backgroundColor="$gray400"
                    zIndex={1}
                />
            )}
            {isLast && !isFirst && (
                <View
                    position="absolute"
                    left={27}
                    top={0}
                    bottom="50%"
                    width={2}
                    backgroundColor="$gray400"
                    zIndex={1}
                />
            )}

            {/* Icon circle */}
            <View
                width={24}
                height={24}
                borderRadius={24}
                backgroundColor={bg}
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                zIndex={2}
            >
                <Icon icon={icon} size={16} color="$black" />
            </View>

            {/* Goal info */}
            <YStack flex={1} gap={4}>
                <BodyMediumRegular>{rule.description}</BodyMediumRegular>
                <XStack gap={4}>
                    {durationLabel != null && (
                        <View
                            backgroundColor={bg}
                            paddingHorizontal={6}
                            paddingVertical={3}
                            borderRadius={4}
                        >
                            <SuperscriptEmphasized
                                textTransform="uppercase"
                                letterSpacing={0.5}
                            >
                                {durationLabel}
                            </SuperscriptEmphasized>
                        </View>
                    )}
                    {amountLabel != null && (
                        <View
                            backgroundColor={lightest}
                            paddingHorizontal={6}
                            paddingVertical={3}
                            borderRadius={4}
                        >
                            <SuperscriptEmphasized
                                color={dark}
                                textTransform="uppercase"
                                letterSpacing={0.5}
                            >
                                {amountLabel}
                            </SuperscriptEmphasized>
                        </View>
                    )}
                </XStack>
            </YStack>

            {/* Date */}
            {dateParts != null && (
                <YStack alignItems="center" paddingHorizontal={12}>
                    <BodyMediumEmphasized>
                        {dateParts.month}
                    </BodyMediumEmphasized>
                    <BodySmallRegular color="#ADADAD">
                        {dateParts.year}
                    </BodySmallRegular>
                </YStack>
            )}
        </XStack>
    );
};

export const TimelineSpendGoalsSlider = ({
    spendCents,
    goalsCents,
    spendPercent,
    goalsPercent,
    remainingCents,
    incomeTotalCents,
    onSlideMove,
    onSlideEnd,
}: {
    spendCents: number;
    goalsCents: number;
    spendPercent: number;
    goalsPercent: number;
    remainingCents: number;
    incomeTotalCents: number;
    onSlideMove: (value: number) => void;
    onSlideEnd: (value: number) => void;
}) => {
    const stepCents = React.useMemo(() => {
        return remainingCents < 200000 ? 1000 : 2000;
    }, [remainingCents]);

    const roundToStep = React.useCallback(
        (value: number) => {
            return Math.round(value / stepCents) * stepCents;
        },
        [stepCents],
    );

    const minSpendCents = getMinSpendCents(incomeTotalCents);
    const maxSpendCents = Math.max(
        minSpendCents,
        remainingCents - MIN_GOALS_CENTS,
    );

    const clampedSpend = Math.max(
        minSpendCents,
        Math.min(spendCents, maxSpendCents),
    );
    const sliderValue = React.useMemo(() => [clampedSpend], [clampedSpend]);

    return (
        <YStack gap={12} paddingHorizontal={16} width="100%">
            <XStack justifyContent="space-between" alignItems="center">
                <XStack gap={6} alignItems="center">
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor="$leaf"
                    />
                    <LabelRegular>Spend</LabelRegular>
                </XStack>
                <XStack gap={6} alignItems="center">
                    <LabelRegular>Goals</LabelRegular>
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor="$petal"
                    />
                </XStack>
            </XStack>

            <Slider
                value={sliderValue}
                onSlideMove={(_, slideValue: number) => {
                    const clamped = Math.max(
                        minSpendCents,
                        Math.min(roundToStep(slideValue), maxSpendCents),
                    );
                    onSlideMove(clamped);
                }}
                onSlideEnd={() => {
                    const clamped = Math.max(
                        minSpendCents,
                        Math.min(roundToStep(spendCents), maxSpendCents),
                    );
                    onSlideEnd(clamped);
                }}
                min={0}
                max={remainingCents}
                step={stepCents}
                width="100%"
                trackActiveColor="$green800"
                trackColor="$orchidPink600"
                thumbSize={24}
            />

            <XStack width="100%" justifyContent="space-between">
                <YStack gap={2}>
                    <BodyLargeEmphasized>
                        {centsToFormattedDollars(spendCents, false)}
                    </BodyLargeEmphasized>
                    <LabelRegular color="$gray1000">
                        {spendPercent}% of income
                    </LabelRegular>
                </YStack>
                <YStack gap={2} alignItems="flex-end">
                    <BodyLargeEmphasized>
                        {centsToFormattedDollars(goalsCents, false)}
                    </BodyLargeEmphasized>
                    <LabelRegular color="$gray1000">
                        {goalsPercent}% of income
                    </LabelRegular>
                </YStack>
            </XStack>
        </YStack>
    );
};

export const FlyingGoalIcon = ({
    icon,
    sourcePosition,
    moveDelay = 0,
    reverse = false,
    onPress,
    selected = false,
    popIn = false,
    popInDelay = 0,
}: {
    icon: { x: number; y: number; type: RuleType };
    sourcePosition?: GoalIconPosition;
    moveDelay?: number;
    reverse?: boolean;
    onPress?: (type: RuleType) => void;
    selected?: boolean;
    popIn?: boolean;
    popInDelay?: number;
}) => {
    const progress = useSharedValue(sourcePosition != null ? 0 : 1);
    const bgFade = useSharedValue(sourcePosition != null && !reverse ? 0 : 1);
    const popScale = useSharedValue(popIn ? 0 : 1);
    const hasAnimated = useRef(false);

    const startX = sourcePosition != null ? sourcePosition.x - icon.x : 0;
    const startY = sourcePosition != null ? sourcePosition.y - icon.y : 0;

    useEffect(() => {
        if (hasAnimated.current || sourcePosition == null) return;
        hasAnimated.current = true;
        progress.value = withDelay(
            moveDelay,
            withTiming(1, {
                duration: 450,
                easing: Easing.out(Easing.cubic),
            }),
        );
        if (!reverse) {
            bgFade.value = withDelay(
                moveDelay + 450,
                withTiming(1, { duration: 200 }),
            );
        }
    }, []);

    useEffect(() => {
        if (!popIn) return;
        popScale.value = withDelay(
            popInDelay,
            withSpring(1, { damping: 12, stiffness: 180 }),
        );
    }, []);

    const bgColor =
        icon.type === RuleType.DebtPayDown ? DEBT_ICONS : GOALS_ICONS;
    const bgColorTransparent = `${bgColor}00`;
    const isFlying = sourcePosition != null;

    const style = useAnimatedStyle(() => ({
        transform: [
            {
                translateX: interpolate(progress.value, [0, 1], [startX, 0]),
            },
            {
                translateY: interpolate(progress.value, [0, 1], [startY, 0]),
            },
            { scale: popScale.value },
        ],
        opacity: 1,
    }));

    const bgStyle = useAnimatedStyle(() => {
        if (!isFlying) {
            return { backgroundColor: bgColor, borderRadius: ICON_SIZE };
        }
        if (reverse) {
            return {
                backgroundColor: interpolateColor(
                    progress.value,
                    [0, 0.2],
                    [bgColor, bgColorTransparent],
                ),
                borderRadius: ICON_SIZE,
            };
        }
        return {
            backgroundColor: interpolateColor(
                bgFade.value,
                [0, 1],
                [bgColorTransparent, bgColor],
            ),
            borderRadius: ICON_SIZE,
        };
    });

    const handlePress =
        onPress != null
            ? (e: { stopPropagation?: () => void }) => {
                  e.stopPropagation?.();
                  onPress(icon.type);
              }
            : undefined;
    const displaySize = selected ? ICON_SELECTED_SIZE : ICON_SIZE;

    const sizeAnim = useSharedValue(displaySize);
    useEffect(() => {
        if (isWeb) return;
        sizeAnim.value = withTiming(displaySize, { duration: 200 });
    }, [displaySize]);
    const nativeSizeStyle = useAnimatedStyle(() => {
        if (isWeb) return {};
        return {
            width: sizeAnim.value,
            height: sizeAnim.value,
        };
    });

    return (
        <Animated.View
            style={[
                {
                    position: 'absolute',
                    left: icon.x - ICON_SIZE / 2,
                    top: icon.y - ICON_SIZE / 2,
                    width: ICON_SIZE + 16,
                    height: ICON_SIZE + 16,
                    marginLeft: -8,
                    marginTop: -8,
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: selected ? 20 : 10,
                    ...(isWeb
                        ? {
                              cursor:
                                  onPress != null
                                      ? ('pointer' as const)
                                      : undefined,
                              transition: `left ${GRAPH_RESHAPE_MS}ms ease-out, top ${GRAPH_RESHAPE_MS}ms ease-out`,
                          }
                        : {}),
                },
                style,
            ]}
            // @ts-expect-error web onClick
            onClick={isWeb ? handlePress : undefined}
        >
            {!isWeb && onPress != null ? (
                <Pressable
                    onPress={() => {
                        onPress(icon.type);
                    }}
                    style={{
                        width: ICON_SIZE + 16,
                        height: ICON_SIZE + 16,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Animated.View
                        style={[
                            {
                                width: displaySize,
                                height: displaySize,
                                alignItems: 'center',
                                justifyContent: 'center',
                            },
                            bgStyle,
                            nativeSizeStyle,
                        ]}
                    >
                        <Icon
                            icon={getRuleIcon(icon.type)}
                            size={16}
                            color={selected ? '$white' : '$black'}
                        />
                    </Animated.View>
                </Pressable>
            ) : (
                <Animated.View
                    style={[
                        {
                            width: displaySize,
                            height: displaySize,
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(isWeb
                                ? {
                                      transition:
                                          'width 200ms ease, height 200ms ease',
                                  }
                                : {}),
                        },
                        bgStyle,
                        nativeSizeStyle,
                    ]}
                >
                    <Icon
                        icon={getRuleIcon(icon.type)}
                        size={16}
                        color={selected ? '$white' : '$black'}
                    />
                </Animated.View>
            )}
        </Animated.View>
    );
};

export const TimelineGraph = ({
    allRules,
    monthlyGoalsCents,
    tall = false,
    hideIcons = false,
    drawLines = true,
    showLabels = true,
    popInIcons = false,
    onIconPress,
    onBackgroundPress,
    selectedRuleType,
}: {
    allRules: RuleCompletionInfo[];
    monthlyGoalsCents?: number;
    tall?: boolean;
    hideIcons?: boolean;
    drawLines?: boolean;
    showLabels?: boolean;
    popInIcons?: boolean;
    onIconPress?: (type: RuleType) => void;
    onBackgroundPress?: () => void;
    selectedRuleType?: RuleType | null;
}) => {
    const [containerHeight, setContainerHeight] = useState(0);
    const effectiveHeight =
        tall && containerHeight > 0 ? containerHeight : GRAPH_HEIGHT;
    const [containerWidth, setContainerWidth] = useState(0);
    const debtData = React.useMemo(
        () => getDebtData(allRules, monthlyGoalsCents),
        [allRules, monthlyGoalsCents],
    );
    const timeAxis = React.useMemo(
        () => computeTimeAxis(debtData?.payoffMonths ?? null, allRules),
        [debtData, allRules],
    );
    const totalAxisMonths = timeAxis.totalMonths;

    const goalsLine = React.useMemo(
        () => getGoalsLineData(allRules, debtData),
        [allRules, debtData],
    );

    const graphW =
        containerWidth > 0
            ? containerWidth - GRAPH_PADDING_LEFT - GRAPH_PADDING_RIGHT
            : 0;
    const graphH = effectiveHeight - GRAPH_PADDING_TOP - GRAPH_PADDING_BOTTOM;

    const yMax = React.useMemo(() => {
        const debtMax = debtData?.totalCents ?? 0;
        const goalsMax = goalsValueAtMonth(
            totalAxisMonths,
            goalsLine.phase1MonthlyCents,
            goalsLine.phase2MonthlyCents,
            debtData?.payoffMonths ?? null,
        );
        return Math.max(debtMax, goalsMax, 1) * Y_AXIS_HEADROOM;
    }, [debtData, goalsLine, totalAxisMonths]);

    const debtPayoffFraction =
        debtData != null ? debtData.payoffMonths / totalAxisMonths : 0;

    const balancePoints = React.useMemo(() => {
        if (debtData == null) return [];
        return computeAmortizationPoints(
            debtData.totalCents,
            debtData.annualRate,
            debtData.payoffMonths,
            AMORTIZATION_POINTS,
        );
    }, [debtData]);

    const timeLabels = timeAxis.labels;

    const debtEndX =
        debtData != null ? GRAPH_PADDING_LEFT + debtPayoffFraction * graphW : 0;
    const debtEndY = GRAPH_PADDING_TOP + graphH;

    const hasGoalsLine = goalsLine.phase1MonthlyCents > 0;

    const [linesVisible, setLinesVisible] = useState(drawLines);
    const prevDrawLinesRef = useRef(drawLines);
    useEffect(() => {
        if (drawLines && !prevDrawLinesRef.current) {
            const timer = setTimeout(() => {
                setLinesVisible(true);
            }, 50);
            prevDrawLinesRef.current = drawLines;
            return () => {
                clearTimeout(timer);
            };
        }
        if (!drawLines) {
            setLinesVisible(false);
        }
        prevDrawLinesRef.current = drawLines;
        return undefined;
    }, [drawLines]);

    const maxPathLength = graphW + graphH + 500;

    const lineOpacity = useSharedValue(linesVisible ? 1 : 0);
    const lineDashOffset = useSharedValue(linesVisible ? 0 : maxPathLength);
    const labelSlide = useSharedValue(showLabels ? 1 : 0);

    useEffect(() => {
        if (linesVisible) {
            lineDashOffset.value = maxPathLength;
            lineOpacity.value = withTiming(1, {
                duration: LINE_DRAW_DURATION,
                easing: Easing.inOut(Easing.ease),
            });
            lineDashOffset.value = withTiming(0, {
                duration: LINE_DRAW_DURATION,
                easing: Easing.inOut(Easing.ease),
            });
        } else {
            lineOpacity.value = 0;
            lineDashOffset.value = maxPathLength;
        }
    }, [linesVisible, maxPathLength]);

    useEffect(() => {
        if (isWeb) return;
        if (showLabels) {
            labelSlide.value = withTiming(1, {
                duration: LABEL_SLIDE_DURATION,
                easing: Easing.out(Easing.ease),
            });
        } else {
            labelSlide.value = 0;
        }
    }, [showLabels]);

    const nativeLabelStyle = useAnimatedStyle(() => {
        if (isWeb) return {};
        return {
            opacity: labelSlide.value,
            transform: [
                {
                    translateY: interpolate(
                        labelSlide.value,
                        [0, 1],
                        [LABEL_SLIDE_DISTANCE, 0],
                    ),
                },
            ],
        };
    });

    const animatedFillProps = useAnimatedProps(() => ({
        opacity: lineOpacity.value,
    }));

    const animatedLineProps = useAnimatedProps(() => ({
        strokeDashoffset: lineDashOffset.value,
    }));

    const goalIcons = React.useMemo(() => {
        if (!hasGoalsLine || graphW <= 0 || containerWidth <= 0) return [];
        const rawIcons = goalsLine.goals
            .filter((g) => g.completionMonths != null)
            .filter((g) => (g.completionMonths as number) <= totalAxisMonths)
            .map((g) => {
                const month = g.completionMonths as number;
                const val = goalsValueAtMonth(
                    month,
                    goalsLine.phase1MonthlyCents,
                    goalsLine.phase2MonthlyCents,
                    debtData?.payoffMonths ?? null,
                );
                const x =
                    GRAPH_PADDING_LEFT + (month / totalAxisMonths) * graphW;
                const y = GRAPH_PADDING_TOP + graphH * (1 - val / yMax);
                const inBounds =
                    x >= 0 &&
                    x <= containerWidth &&
                    y >= 0 &&
                    y <= effectiveHeight;
                if (!inBounds) return null;
                return { x, y, type: g.type };
            })
            .filter((v): v is NonNullable<typeof v> => v != null);

        // Goals that finish on (nearly) the same date resolve to the same
        // point, so their icons stack and only the top one stays visible and
        // tappable. Fan any overlapping icons out horizontally — centered on
        // the shared point and clamped to stay on-screen — so each goal keeps
        // a distinct, independently tappable marker (clustered side-by-side).
        const CLUSTER_SPACING = ICON_SIZE + 4;
        const sorted = [...rawIcons].sort((a, b) => a.x - b.x);
        const result: typeof sorted = [];
        let clusterStart = 0;
        const flushCluster = (endExclusive: number) => {
            const group = sorted.slice(clusterStart, endExclusive);
            if (group.length <= 1) {
                result.push(...group);
                return;
            }
            const centerX =
                group.reduce((sum, g) => sum + g.x, 0) / group.length;
            const span = (group.length - 1) * CLUSTER_SPACING;
            const minCenter = ICON_SIZE / 2 + span / 2;
            const maxCenter = containerWidth - ICON_SIZE / 2 - span / 2;
            const clampedCenter = Math.min(
                Math.max(centerX, minCenter),
                maxCenter,
            );
            group.forEach((g, idx) => {
                const offset = (idx - (group.length - 1) / 2) * CLUSTER_SPACING;
                result.push({ ...g, x: clampedCenter + offset });
            });
        };
        for (let i = 1; i <= sorted.length; i++) {
            const prev = sorted[i - 1];
            const cur = sorted[i];
            if (
                cur == null ||
                (prev != null && cur.x - prev.x >= CLUSTER_SPACING)
            ) {
                flushCluster(i);
                clusterStart = i;
            }
        }
        return result;
    }, [
        hasGoalsLine,
        graphW,
        containerWidth,
        goalsLine,
        debtData,
        totalAxisMonths,
        graphH,
        yMax,
        effectiveHeight,
    ]);

    return (
        <YStack
            {...(tall ? { flex: 1 } : { height: effectiveHeight })}
            width="100%"
            paddingHorizontal={0}
            onPress={onBackgroundPress}
            onLayout={(e) => {
                setContainerWidth(e.nativeEvent.layout.width);
                if (tall) {
                    setContainerHeight(e.nativeEvent.layout.height);
                }
            }}
        >
            {/* Legend */}
            <Animated.View
                style={[
                    {
                        position: 'absolute',
                        left: 16,
                        top: 16,
                        gap: 2,
                        zIndex: 2,
                        ...(isWeb && {
                            opacity: showLabels ? 1 : 0,
                            transform: showLabels
                                ? 'translateY(0px)'
                                : `translateY(${LABEL_SLIDE_DISTANCE}px)`,
                            transition: showLabels
                                ? `opacity ${LABEL_SLIDE_DURATION}ms ease, transform ${LABEL_SLIDE_DURATION}ms ease-out`
                                : 'none',
                        }),
                    },
                    nativeLabelStyle,
                ]}
            >
                <LabelRegular color={GOALS_LABEL}>Goals over time</LabelRegular>
                {debtData != null && (
                    <LabelRegular color={DEBT_LABEL}>
                        Debt over time
                    </LabelRegular>
                )}
            </Animated.View>

            {/* SVG graph */}
            {containerWidth > 0 && graphW > 0 && (
                <View
                    position="absolute"
                    left={GRAPH_PADDING_LEFT}
                    top={GRAPH_PADDING_TOP}
                    width={graphW}
                    height={graphH}
                >
                    <Svg
                        width={graphW}
                        height={graphH}
                        viewBox={`0 0 ${graphW} ${graphH}`}
                    >
                        {/* @ts-expect-error react-native-svg Defs typing mismatch */}
                        <Defs>
                            <LinearGradient
                                id="goalsGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <Stop
                                    offset="0"
                                    stopColor={GOALS_BG_DARK}
                                    stopOpacity={0.7}
                                />
                                <Stop
                                    offset="0.6"
                                    stopColor={GOALS_BG_DARK}
                                    stopOpacity={0.3}
                                />
                                <Stop
                                    offset="1"
                                    stopColor={GOALS_BG_LIGHT}
                                    stopOpacity={0}
                                />
                            </LinearGradient>
                            <LinearGradient
                                id="debtGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <Stop
                                    offset="0"
                                    stopColor={DEBT_BG_DARK}
                                    stopOpacity={0.8}
                                />
                                <Stop
                                    offset="0.6"
                                    stopColor={DEBT_BG_DARK}
                                    stopOpacity={0.35}
                                />
                                <Stop
                                    offset="1"
                                    stopColor={DEBT_BG_LIGHT}
                                    stopOpacity={0}
                                />
                            </LinearGradient>
                        </Defs>

                        {/* Goals fill + line */}
                        {hasGoalsLine && (
                            <>
                                <AnimatedPath
                                    animatedProps={animatedFillProps}
                                    d={buildGoalsFillPath(
                                        graphW,
                                        graphH,
                                        totalAxisMonths,
                                        yMax,
                                        goalsLine.phase1MonthlyCents,
                                        goalsLine.phase2MonthlyCents,
                                        debtData?.payoffMonths ?? null,
                                    )}
                                    fill="url(#goalsGradient)"
                                />
                                <AnimatedPath
                                    animatedProps={animatedLineProps}
                                    d={buildGoalsLinePath(
                                        graphW,
                                        graphH,
                                        totalAxisMonths,
                                        yMax,
                                        goalsLine.phase1MonthlyCents,
                                        goalsLine.phase2MonthlyCents,
                                        debtData?.payoffMonths ?? null,
                                    )}
                                    stroke={GOALS_LINE}
                                    strokeWidth={2.5}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray={maxPathLength}
                                />
                            </>
                        )}

                        {/* Debt fill + line */}
                        {debtData != null && balancePoints.length > 0 && (
                            <>
                                <AnimatedPath
                                    animatedProps={animatedFillProps}
                                    d={buildAmortizationFillPath(
                                        graphW,
                                        graphH,
                                        debtPayoffFraction,
                                        balancePoints,
                                        yMax,
                                    )}
                                    fill="url(#debtGradient)"
                                />
                                <AnimatedPath
                                    animatedProps={animatedLineProps}
                                    d={buildAmortizationPath(
                                        graphW,
                                        graphH,
                                        debtPayoffFraction,
                                        balancePoints,
                                        yMax,
                                    )}
                                    stroke={DEBT_LINE}
                                    strokeWidth={2.5}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeDasharray={maxPathLength}
                                />
                            </>
                        )}
                    </Svg>
                </View>
            )}

            {/* Goal completion icons */}
            {!hideIcons &&
                goalIcons.map((icon, i) => (
                    <FlyingGoalIcon
                        key={`goal-icon-${i}`}
                        icon={icon}
                        onPress={onIconPress}
                        selected={selectedRuleType === icon.type}
                        popIn={popInIcons}
                        popInDelay={i * 80}
                    />
                ))}

            {/* Debt endpoint icon */}
            {!hideIcons && debtData != null && containerWidth > 0 && (
                <FlyingGoalIcon
                    key="debt-icon"
                    icon={{
                        x: debtEndX,
                        y: debtEndY,
                        type: RuleType.DebtPayDown,
                    }}
                    onPress={onIconPress}
                    selected={selectedRuleType === RuleType.DebtPayDown}
                    popIn={popInIcons}
                    popInDelay={goalIcons.length * 80}
                />
            )}

            {/* X-axis labels */}
            <Animated.View
                style={[
                    {
                        position: 'absolute',
                        bottom: 0,
                        left: 16,
                        right: 16,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        paddingBottom: 8,
                        ...(isWeb && {
                            opacity: showLabels ? 1 : 0,
                            transform: showLabels
                                ? 'translateY(0px)'
                                : `translateY(${LABEL_SLIDE_DISTANCE}px)`,
                            transition: showLabels
                                ? `opacity ${LABEL_SLIDE_DURATION}ms ease, transform ${LABEL_SLIDE_DURATION}ms ease-out`
                                : 'none',
                        }),
                    },
                    nativeLabelStyle,
                ]}
            >
                {timeLabels.map((label) => (
                    <LabelEmphasized key={label} color="$gray800">
                        {label}
                    </LabelEmphasized>
                ))}
            </Animated.View>
        </YStack>
    );
};

export const TimelineView = ({
    incomeTotalCents,
    spendTotalCents,
    goalsTotalCents,
    ruleSetDisplays,
    onLayout,
    onSpendSlideMove,
    onSpendSlideEnd,
    graphOnly = false,
    hideIcons = false,
    drawLines = true,
    popInIcons = false,
    onContentLayout,
}: {
    incomeTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
    ruleSetDisplays: RuleSetDisplay[];
    onLayout: (e: LayoutChangeEvent) => void;
    onSpendSlideMove?: (spendCents: number) => void;
    onSpendSlideEnd?: (spendCents: number) => void;
    graphOnly?: boolean;
    hideIcons?: boolean;
    drawLines?: boolean;
    popInIcons?: boolean;
    onContentLayout?: (layout: { y: number; height: number }) => void;
}) => {
    const allRules = ruleSetDisplays
        .flatMap((rs) => rs.rules)
        .sort((a, b) => {
            const aRetire = a.type === RuleType.Retirement ? 1 : 0;
            const bRetire = b.type === RuleType.Retirement ? 1 : 0;
            if (aRetire !== bRetire) return aRetire - bRetire;
            const aDate =
                a.completionDate != null
                    ? parseDate(a.completionDate).getTime()
                    : Infinity;
            const bDate =
                b.completionDate != null
                    ? parseDate(b.completionDate).getTime()
                    : Infinity;
            return aDate - bDate;
        });
    const debtPayoffMonths = React.useMemo(
        () => getDebtData(allRules, goalsTotalCents)?.payoffMonths ?? null,
        [allRules, goalsTotalCents],
    );
    const [selectedRule, setSelectedRule] = useState<RuleType | null>(null);
    const handleIconPress = useCallback((type: RuleType) => {
        setSelectedRule((prev) => (prev === type ? null : type));
    }, []);
    const handleDeselect = useCallback(() => {
        setSelectedRule(null);
    }, []);
    const remainingCents = spendTotalCents + goalsTotalCents;
    const spendPercent =
        incomeTotalCents > 0
            ? Math.round((spendTotalCents / incomeTotalCents) * 100)
            : 0;
    const goalsPercent =
        incomeTotalCents > 0
            ? Math.round((goalsTotalCents / incomeTotalCents) * 100)
            : 0;

    const allReady = drawLines;
    // Background renders immediately so the container is visible during
    // parent enter animations; only animated parts wait for drawLines.
    const [bgVisible, setBgVisible] = useState(true);
    const [linesReady, setLinesReady] = useState(allReady);
    const [labelsVisible, setLabelsVisible] = useState(allReady);
    const [contentVisible, setContentVisible] = useState(allReady);
    const prevDrawLinesForContent = useRef(allReady);

    const contentSlideProgress = useSharedValue(allReady ? 1 : 0);

    useEffect(() => {
        if (allReady && !prevDrawLinesForContent.current) {
            const timers: Array<ReturnType<typeof setTimeout>> = [];

            timers.push(
                setTimeout(() => {
                    setBgVisible(true);
                }, 50),
            );

            timers.push(
                setTimeout(() => {
                    setLinesReady(true);
                }, BG_FADE_DURATION),
            );

            timers.push(
                setTimeout(() => {
                    setLabelsVisible(true);
                }, BG_FADE_DURATION + LINE_DRAW_DURATION),
            );

            timers.push(
                setTimeout(
                    () => {
                        setContentVisible(true);
                        if (!isWeb) {
                            contentSlideProgress.value = withTiming(1, {
                                duration: CONTENT_SLIDE_DURATION,
                                easing: Easing.out(Easing.ease),
                            });
                        }
                    },
                    BG_FADE_DURATION +
                        LINE_DRAW_DURATION +
                        LABEL_SLIDE_DURATION,
                ),
            );

            prevDrawLinesForContent.current = allReady;
            return () => {
                timers.forEach(clearTimeout);
            };
        }
        if (!allReady) {
            setLinesReady(false);
            setLabelsVisible(false);
            setContentVisible(false);
            contentSlideProgress.value = 0;
        }
        prevDrawLinesForContent.current = allReady;
        return undefined;
    }, [allReady]);

    const nativeContentSlideStyle = useAnimatedStyle(() => {
        if (isWeb) return {};
        return {
            opacity: contentSlideProgress.value,
            transform: [
                {
                    translateY: interpolate(
                        contentSlideProgress.value,
                        [0, 1],
                        [CONTENT_SLIDE_DISTANCE, 0],
                    ),
                },
            ],
        };
    });

    const handleCardLayout = useCallback(
        (e: LayoutChangeEvent) => {
            onLayout(e);
            onContentLayout?.({
                y: e.nativeEvent.layout.y,
                height: e.nativeEvent.layout.height,
            });
        },
        [onLayout, onContentLayout],
    );

    return (
        <YStack
            width="100%"
            {...(graphOnly ? { flex: 1 } : {})}
            onLayout={handleCardLayout}
            backgroundColor={bgVisible ? '$white' : 'transparent'}
            borderRadius={24}
            overflow="hidden"
            style={{
                ...(graphOnly
                    ? {
                          shadowColor: 'rgba(0, 0, 0, 0.06)',
                          shadowOffset: { width: 0, height: 16 },
                          shadowOpacity: 1,
                          shadowRadius: 24,
                      }
                    : {
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 8 },
                          shadowOpacity: 0.06,
                          shadowRadius: 24,
                      }),
                transition: `background-color ${BG_FADE_DURATION}ms ease`,
            }}
            {...Shadows.SMALL}
        >
            {/* Graph */}
            <TimelineGraph
                allRules={allRules}
                monthlyGoalsCents={goalsTotalCents}
                tall={graphOnly}
                hideIcons={hideIcons}
                drawLines={linesReady}
                showLabels={labelsVisible}
                popInIcons={popInIcons}
                {...(!graphOnly && {
                    onIconPress: handleIconPress,
                    onBackgroundPress: handleDeselect,
                    selectedRuleType: selectedRule,
                })}
            />

            {!graphOnly && (
                <>
                    {/* Spend / Goals slider */}
                    <Animated.View
                        style={[
                            {
                                ...(isWeb && {
                                    opacity: contentVisible ? 1 : 0,
                                    transform: contentVisible
                                        ? 'translateY(0px)'
                                        : `translateY(${CONTENT_SLIDE_DISTANCE}px)`,
                                    transition: contentVisible
                                        ? `opacity ${CONTENT_SLIDE_DURATION}ms ease 0ms, transform ${CONTENT_SLIDE_DURATION}ms ease-out 0ms`
                                        : 'none',
                                }),
                            },
                            nativeContentSlideStyle,
                        ]}
                    >
                        <TimelineSpendGoalsSlider
                            spendCents={spendTotalCents}
                            goalsCents={goalsTotalCents}
                            spendPercent={spendPercent}
                            goalsPercent={goalsPercent}
                            remainingCents={remainingCents}
                            incomeTotalCents={incomeTotalCents}
                            onSlideMove={onSpendSlideMove ?? (() => {})}
                            onSlideEnd={onSpendSlideEnd ?? (() => {})}
                        />
                    </Animated.View>

                    {/* Goals timeline list */}
                    <YStack
                        paddingHorizontal={16}
                        paddingBottom={16}
                        paddingTop={24}
                        position="relative"
                    >
                        {allRules.map((rule, i) => (
                            <Animated.View
                                key={`${rule.type}-${i}`}
                                style={[
                                    {
                                        ...(isWeb && {
                                            opacity: contentVisible ? 1 : 0,
                                            transform: contentVisible
                                                ? 'translateY(0px)'
                                                : `translateY(${CONTENT_SLIDE_DISTANCE}px)`,
                                            transition: contentVisible
                                                ? `opacity ${CONTENT_SLIDE_DURATION}ms ease ${CONTENT_STAGGER_DELAY * (i + 1)}ms, transform ${CONTENT_SLIDE_DURATION}ms ease-out ${CONTENT_STAGGER_DELAY * (i + 1)}ms`
                                                : 'none',
                                        }),
                                    },
                                    nativeContentSlideStyle,
                                ]}
                            >
                                <TimelineGoalRow
                                    rule={rule}
                                    isFirst={i === 0}
                                    isLast={i === allRules.length - 1}
                                    selected={selectedRule === rule.type}
                                    debtPayoffMonths={debtPayoffMonths}
                                />
                            </Animated.View>
                        ))}
                    </YStack>
                </>
            )}
        </YStack>
    );
};
